/**
 * Firebase Remote Config flag reader.
 *
 * Reads every Remote Config flag at app startup and dispatches the values into
 * the `remoteConfig` Redux slice. Consumers subscribe via per-flag selectors
 * from `./remote-config-slice`.
 */
import { CustomSignals, fetchAndActivate, getValue, RemoteConfig, setCustomSignals } from 'firebase/remote-config';
import { store } from 'core/store/store';
import { remoteConfig } from './firebase';
import defaultConfig from './default-remote-config.json'; // downloaded from firebase
import { configFirebase } from '../environment-variables';
import { RemoteConfigState, setFlags } from './remote-config-slice';

type RemoteConfigFlags = keyof typeof defaultConfig;

const getRemoteConfigFlag = (key: RemoteConfigFlags, rc: RemoteConfig): boolean => getValue(rc, key).asBoolean();
const getRemoteConfigNumber = (key: RemoteConfigFlags, rc: RemoteConfig): number => getValue(rc, key).asNumber();
const getRemoteConfigString = (key: RemoteConfigFlags, rc: RemoteConfig): string => getValue(rc, key).asString();

const activeSignalKeys = new Set<string>();

export const readAllFlags = (rc: RemoteConfig) => {
  const values: RemoteConfigState = {
    /**
     * Boolean toggling the full-screen maintenance takeover that blocks all
     * app content while ops work is in progress.
     * @default false
     */
    maintenanceScreenEnabled: getRemoteConfigFlag('maintenance_screen_enabled', rc),
    /**
     * Number in ms controlling how frequently the assets map re-fetches
     * marker positions. `0` disables automatic polling.
     * @default 0
     */
    getMarkersPollingInterval: getRemoteConfigNumber('get_markers_polling_interval', rc),
    /**
     * Number capping how many customer dashboard widgets a user may add.
     * Consumed by the "Add Widget" gating logic.
     * @default 16
     * @since 2024-04-30
     */
    numberOfCustomerWidgetsAllowed: getRemoteConfigNumber('no_of_customer_widgets_allowed', rc),
    /**
     * Boolean toggling the Warranty block in the Asset Details section.
     * @default false
     * @since 2025-05-15
     */
    displayWarranty: getRemoteConfigFlag('display_warranty', rc),
    /**
     * String holding the feedback survey URL surfaced in the nav dropdown
     * and the FeedbackLink component. Empty string hides the feedback entry.
     * Inferred from call sites — not on the Confluence journal.
     * @default ""
     */
    feedbackUrl: getRemoteConfigString('feedback_url', rc),
    /**
     * Number in ms controlling how frequently dashboard widgets re-fetch
     * their backing data (customer widgets, user-defined widgets, custom
     * widget modal preview).
     * Inferred from call sites — not on the Confluence journal.
     * @default 60000
     */
    dashboardWidgetsPollingInterval: getRemoteConfigNumber('dashboard_widgets_polling_interval', rc),
    /**
     * Boolean historically toggling a fatal-error filter view.
     * Currently no consumers in the codebase; candidate for removal in a
     * follow-up ticket.
     * @default false
     */
    displayFatalErrorFilter: getRemoteConfigFlag('display_fatal_error_filter', rc),
    /**
     * JSON blob (stringified) parsed by `use-dealers-report-config` into
     * `{ report, users }`. `report` is the Power BI URL, `users` is a
     * string array of allowed user IDs that gates the /dealers page.
     * Inferred from call sites — not on the Confluence journal.
     * @default ""
     */
    dealersReport: getRemoteConfigString('dealers_report', rc),
    /**
     * Number capping how many user-defined dashboard widgets a user may add.
     * @default 10
     * @since 2025-09-04
     */
    numberOfUserDefinedWidgetsAllowed: getRemoteConfigNumber('no_of_user_defined_widgets_allowed', rc),
    /**
     * Allowed-users CSV read via `useAllowedUsersConfig`. Switches the Map page
     * to the streamed deck.gl map (all assets, no 5000 cap) for the given users;
     * everyone else keeps the legacy marker map.
     * @default ""
     * @since 2026-10-26
     */
    displayStreamingMap: getRemoteConfigString('display_streaming_map', rc),
    /**
     * Allowed-users CSV read via `useAllowedUsersConfig`. Enables the
     * grouped "list" dashboard widgets for the given users.
     * @default ""
     * @since 2026-03-06
     */
    displayUserDefinedGroupedListWidgets: getRemoteConfigString('display_grouped_list_widgets', rc),
    /**
     * JSON blob (stringified) parsed by `selectAppStatusConfig` into
     * `{ statusMode, bannerMessage, bannerSeverity, lockdownTitle,
     * lockdownMessage }`. Drives the OME incident-communication banner and
     * lockdown screen.
     * @default ""
     * @since 2026-04-20
     */
    appStatusConfig: getRemoteConfigString('app_status_config', rc),
    /**
     * Boolean toggling the incident-communication functionality (banner +
     * lockdown) driven by `appStatusConfig`.
     * @default false
     * @since 2026-04-20
     */
    incidentCommunicationEnabled: getRemoteConfigFlag('incident_communication_enabled', rc),
    /**
     * Boolean toggling the charging station, reference station and docking
     * point markers on the asset details map

     * @default false
     * @since 2026-09-22
     */
    enableEposArea: getRemoteConfigFlag('enable_epos_area', rc),
    /**
     * Boolean toggling the favorite companies filter in the dashboard.
     * @default false
     */
    enableFavoritCompaniesFilter: getRemoteConfigFlag('enable_favorit_companies_filter', rc),
    /**
     * Boolean toggling the draggable gutter between the map and the info
     * panel on the asset details page on all screen sizes.
     * @default false
     * @since 2026-10-21
     */
    enableGutter: getRemoteConfigFlag('enable_gutter', rc),
  };
  store.dispatch(setFlags(values));
};

export const initRemoteConfig = async (customSignals?: CustomSignals) => {
  const rc = await remoteConfig();

  if (rc) {
    const configuredInterval = configFirebase.remoteConfigFetchInterval
      ? Number(configFirebase.remoteConfigFetchInterval)
      : rc.settings.minimumFetchIntervalMillis;
    rc.defaultConfig = defaultConfig;

    if (customSignals) {
      const nextKeys = new Set(Object.keys(customSignals));
      const droppedKeys = Array.from(activeSignalKeys).filter((key) => !nextKeys.has(key));
      const signalsToSet: CustomSignals = { ...customSignals };
      droppedKeys.forEach((key) => {
        signalsToSet[key] = null;
      });
      await setCustomSignals(rc, signalsToSet);
      activeSignalKeys.clear();
      nextKeys.forEach((key) => activeSignalKeys.add(key));
    }

    rc.settings.minimumFetchIntervalMillis = 0;
    await fetchAndActivate(rc);
    rc.settings.minimumFetchIntervalMillis = configuredInterval;

    readAllFlags(rc);
  }
};
